import { createAction } from '@reduxjs/toolkit';
import type { IErrorType } from '../../constants/types';
import type {
    ICampaignHostBillingHistoryResponse,
    ICampaignHostCreationAccess,
    ICampaignHostPackage,
    ICampaignHostPricingResponse,
    ICampaignHostSubscription,
    ICampaignHostVerifyPaystackResult,
} from '../../types/global/campaignHostSubscription.types';
import type {
    IGrantCampaignHostEnterprisePayload,
    IGrantCampaignHostEnterpriseResult,
    ICampaignHostEnterprisePackage,
} from '../../types/global/campaignHostAdmin.types';
import {
    types,
    type ICancelSubscriptionPayload,
    type IFetchBillingHistoryPayload,
    type IPurchaseLtdPayload,
    type ISubscribeMonthlyPayload,
    type IUpdatePaymentMethodPayload,
    type IVerifyCampaignHostPaystackPayload,
} from './types';

export const fetchCampaignHostPackages = createAction(types.FETCH_PACKAGES);
export const fetchCampaignHostPackagesSuccess = createAction<ICampaignHostPackage[]>(types.FETCH_PACKAGES_SUCCESS);
export const fetchCampaignHostPackagesFail = createAction<IErrorType>(types.FETCH_PACKAGES_FAIL);

export const fetchCampaignHostPricing = createAction(types.FETCH_PRICING);
export const fetchCampaignHostPricingSuccess = createAction<ICampaignHostPricingResponse>(types.FETCH_PRICING_SUCCESS);
export const fetchCampaignHostPricingFail = createAction<IErrorType>(types.FETCH_PRICING_FAIL);

export const fetchCampaignHostMine = createAction(types.FETCH_MINE);
export const fetchCampaignHostMineSuccess = createAction<ICampaignHostSubscription | null>(types.FETCH_MINE_SUCCESS);
export const fetchCampaignHostMineFail = createAction<IErrorType>(types.FETCH_MINE_FAIL);

export const fetchCampaignHostCreationAccess = createAction(types.FETCH_CREATION_ACCESS);
export const fetchCampaignHostCreationAccessSuccess = createAction<ICampaignHostCreationAccess>(
    types.FETCH_CREATION_ACCESS_SUCCESS,
);
export const fetchCampaignHostCreationAccessFail = createAction<IErrorType>(types.FETCH_CREATION_ACCESS_FAIL);

export const fetchCampaignHostBillingHistory = createAction<IFetchBillingHistoryPayload | undefined>(
    types.FETCH_BILLING_HISTORY,
);
export const fetchCampaignHostBillingHistorySuccess = createAction<ICampaignHostBillingHistoryResponse>(
    types.FETCH_BILLING_HISTORY_SUCCESS,
);
export const fetchCampaignHostBillingHistoryFail = createAction<IErrorType>(types.FETCH_BILLING_HISTORY_FAIL);

export const subscribeCampaignHostMonthly = createAction<ISubscribeMonthlyPayload>(types.SUBSCRIBE_MONTHLY);
export const subscribeCampaignHostMonthlySuccess = createAction<{
    subscription: ICampaignHostSubscription | null;
    clientSecret?: string | null;
}>(types.SUBSCRIBE_MONTHLY_SUCCESS);
export const subscribeCampaignHostMonthlyFail = createAction<IErrorType>(types.SUBSCRIBE_MONTHLY_FAIL);

export const purchaseCampaignHostLtd = createAction<IPurchaseLtdPayload>(types.PURCHASE_LTD);
export const purchaseCampaignHostLtdSuccess = createAction<{
    clientSecret: string;
    paymentIntentId: string;
    subscription: ICampaignHostSubscription | null;
}>(types.PURCHASE_LTD_SUCCESS);
export const purchaseCampaignHostLtdFail = createAction<IErrorType>(types.PURCHASE_LTD_FAIL);

export const cancelCampaignHostSubscription = createAction<ICancelSubscriptionPayload>(types.CANCEL_SUBSCRIPTION);
export const cancelCampaignHostSubscriptionSuccess = createAction<ICampaignHostSubscription>(
    types.CANCEL_SUBSCRIPTION_SUCCESS,
);
export const cancelCampaignHostSubscriptionFail = createAction<IErrorType>(types.CANCEL_SUBSCRIPTION_FAIL);

export const updateCampaignHostPaymentMethod = createAction<IUpdatePaymentMethodPayload>(types.UPDATE_PAYMENT_METHOD);
export const updateCampaignHostPaymentMethodSuccess = createAction<ICampaignHostSubscription>(
    types.UPDATE_PAYMENT_METHOD_SUCCESS,
);
export const updateCampaignHostPaymentMethodFail = createAction<IErrorType>(types.UPDATE_PAYMENT_METHOD_FAIL);

export const setCampaignHostTierModalOpen = createAction<boolean>(types.SET_TIER_MODAL_OPEN);
export const clearCampaignHostCheckout = createAction(types.CLEAR_CHECKOUT);

export const verifyCampaignHostPaystack = createAction<IVerifyCampaignHostPaystackPayload>(types.VERIFY_PAYSTACK);
export const verifyCampaignHostPaystackSuccess = createAction<ICampaignHostVerifyPaystackResult>(
    types.VERIFY_PAYSTACK_SUCCESS,
);
export const verifyCampaignHostPaystackFail = createAction<IErrorType>(types.VERIFY_PAYSTACK_FAIL);

export const fetchCampaignHostEnterprisePackage = createAction(types.FETCH_ENTERPRISE_PACKAGE);
export const fetchCampaignHostEnterprisePackageSuccess = createAction<ICampaignHostEnterprisePackage>(
    types.FETCH_ENTERPRISE_PACKAGE_SUCCESS,
);
export const fetchCampaignHostEnterprisePackageFail = createAction<IErrorType>(types.FETCH_ENTERPRISE_PACKAGE_FAIL);

export const grantCampaignHostEnterprise = createAction<IGrantCampaignHostEnterprisePayload>(types.GRANT_ENTERPRISE);
export const grantCampaignHostEnterpriseSuccess = createAction<IGrantCampaignHostEnterpriseResult>(
    types.GRANT_ENTERPRISE_SUCCESS,
);
export const grantCampaignHostEnterpriseFail = createAction<IErrorType>(types.GRANT_ENTERPRISE_FAIL);
export const clearCampaignHostEnterpriseGrant = createAction(types.CLEAR_ENTERPRISE_GRANT);

export const submitCampaignHostEnterpriseSupport = createAction<
    import('../../utils/campaignHostEnterpriseSupportApi').ICampaignHostEnterpriseSupportPayload
>(types.SUBMIT_ENTERPRISE_SUPPORT);
export const submitCampaignHostEnterpriseSupportSuccess = createAction(types.SUBMIT_ENTERPRISE_SUPPORT_SUCCESS);
export const submitCampaignHostEnterpriseSupportFail = createAction<IErrorType>(types.SUBMIT_ENTERPRISE_SUPPORT_FAIL);
